import axios from 'axios';
import configService from './utils/configService';
import fileService from './utils/fileService';

const API_KEY = process.env.REACT_APP_FOOTBALL_API_KEY;

class ApiService {
  constructor(apiKey, host) {
    this.apiKey = apiKey || API_KEY;
    this.host = host || 'v3.football.api-sports.io';
    this.baseURL = `https://${this.host}`;
    this.headers = {
      'x-rapidapi-key': this.apiKey,
      'x-rapidapi-host': this.host
    };
  }

  // Генерирует имя файла для сохранения на основе endpoint и параметров
  getFileName(endpoint, params) {
    // Удаляем ведущий слеш для имени файла
    const cleanEndpoint = endpoint.replace(/^\//, '').replace(/\//g, '_');
    
    // Преобразуем параметры в строку для имени файла
    let paramString = '';
    if (params && Object.keys(params).length > 0) {
      paramString = '_' + Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${key}-${params[key]}`)
        .join('_');
    }
    
    return `${cleanEndpoint}${paramString}.json`;
  }

  // Загружает данные из файла
  loadFromFile(endpoint, params) {
    const fileName = this.getFileName(endpoint, params);
    console.log(`Попытка загрузки данных из файла: ${fileName}`);
    
    return fileService.loadData(fileName);
  }

  // Сохраняет данные в файл
  saveToFile(endpoint, params, data) {
    const fileName = this.getFileName(endpoint, params);
    console.log(`Сохранение данных в файл: ${fileName}`);
    
    return fileService.saveData(fileName, data);
  }

  async get(endpoint, params = {}) {
    console.log(`Запрос ${endpoint} с параметрами:`, params);
    
    // Шаг 1: Проверяем кэш перед запросом к API
    const cachedData = configService.getCachedData(endpoint, params);
    if (cachedData) {
      console.log(`✅ Используем данные из кэша для ${endpoint}`);
      return {
        status: 'success',
        data: cachedData,
        fromCache: true
      };
    }
    
    // Шаг 2: Проверяем наличие данных в файле
    const fileData = this.loadFromFile(endpoint, params);
    if (fileData) {
      console.log(`✅ Используем данные из файла для ${endpoint}`);
      
      // Сохраняем данные из файла в кэш для быстрого доступа
      if (fileData) {
        console.log(`Сохраняем данные из файла в кэш для ${endpoint}`);
        configService.saveApiData(endpoint, params, fileData);
        
        // Сохраняем в специализированные кэши в зависимости от типа запроса
        if (fileData.response && Array.isArray(fileData.response)) {
          this.saveToSpecializedCache(endpoint, params, fileData);
        }
      }
      
      return {
        status: 'success',
        data: fileData,
        fromFile: true
      };
    }
    
    console.log(`❌ Данные не найдены в кэше и файлах для ${endpoint}, делаем запрос к API`);

    // Шаг 3: Если данных нет в кэше и файле, делаем запрос к API
    try {
      const url = new URL(`${this.baseURL}${endpoint}`);
      
      // Add params to URL
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key]);
        }
      });
      
      console.log(`📡 Отправка запроса к API: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Ошибка API: ${response.status} ${response.statusText} - ${errorText}`);
        return {
          status: 'error',
          message: `API Error: ${response.status} ${response.statusText} - ${errorText}`,
          fromCache: false,
          fromFile: false
        };
      }
      
      const data = await response.json();
      console.log(`✅ Получен успешный ответ от API для ${endpoint}`);
      
      // Шаг 4: Сохраняем результат в кэш и в файл немедленно
      if (data) {
        console.log(`Попытка сохранить данные API в кэш и файл для ${endpoint}`);
        
        // Сохраняем в общий кэш API
        const savedToCache = configService.saveApiData(endpoint, params, data);
        
        // Сохраняем в файл
        const savedToFile = this.saveToFile(endpoint, params, data);
        
        // Сохраняем в специализированные кэши
        if (data.response && Array.isArray(data.response)) {
          this.saveToSpecializedCache(endpoint, params, data);
        }
        
        if (savedToCache) {
          console.log(`💾 Данные успешно сохранены в кэш для ${endpoint}`);
        } else {
          console.warn(`⚠️ Не удалось сохранить данные в кэш для ${endpoint}`);
        }
        
        if (savedToFile) {
          console.log(`💾 Данные успешно сохранены в файл для ${endpoint}`);
        } else {
          console.warn(`⚠️ Не удалось сохранить данные в файл для ${endpoint}`);
        }
      } else {
        console.warn(`⚠️ Получены пустые данные от API для ${endpoint}, не сохраняем`);
      }
      
      return {
        status: 'success',
        data,
        fromCache: false,
        fromFile: false
      };
    } catch (error) {
      console.error(`❌ Ошибка при запросе к API ${endpoint}:`, error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch data',
        fromCache: false,
        fromFile: false
      };
    }
  }
  
  // Сохраняет данные в специализированные кэши
  saveToSpecializedCache(endpoint, params, data) {
    if (!data.response || !Array.isArray(data.response)) return;
    
    // Команды
    if (endpoint === '/teams' && params.league) {
      console.log(`Сохраняем данные команд для лиги ${params.league} в специальный кэш`);
      configService.saveTeams(params.league, data);
    }
    
    // Матчи
    if (endpoint === '/fixtures' && params.league && params.season) {
      console.log(`Сохраняем данные матчей для лиги ${params.league}, сезон ${params.season} в специальный кэш`);
      configService.saveFixtures(params, data);
    }
    
    // Турнирные таблицы
    if (endpoint === '/standings' && params.league && params.season) {
      console.log(`Сохраняем данные турнирной таблицы лиги ${params.league}, сезон ${params.season} в специальный кэш`);
      configService.saveStandings(params.league, params.season, data);
    }
    
    // Статистика команды
    if (endpoint === '/teams/statistics' && params.league && params.season && params.team) {
      console.log(`Сохраняем статистику команды ${params.team} в лиге ${params.league}, сезон ${params.season}`);
      configService.saveStatistics(params.league, params.season, params.team, data);
    }
    
    // Head to Head
    if (endpoint === '/fixtures/headtohead' && params.h2h) {
      console.log(`Сохраняем статистику очных встреч команд ${params.h2h} в специальный кэш`);
      configService.saveHeadToHead(params.h2h, data);
    }
  }

  async getLeagues() {
    console.log('Вызов метода getLeagues()');
    
    try {
      // Проверяем специальный кэш для лиг сначала
      const cachedLeagues = configService.getLeagues();
      if (cachedLeagues && Array.isArray(cachedLeagues) && cachedLeagues.length > 0) {
        console.log('✅ Используем специальный кэш для лиг, найдено лиг:', cachedLeagues.length);
        return {
          status: 'success',
          data: { response: cachedLeagues },
          fromCache: true
        };
      }
      
      console.log('❌ Данные в специальном кэше лиг не найдены или пусты');
      
      // Проверяем общий кэш API
      const cachedData = configService.getCachedData('/leagues', {});
      if (cachedData) {
        console.log('✅ Используем данные лиг из общего кэша API');
        
        // Сохраняем в специальный кэш, если есть response и это массив
        if (cachedData.response && Array.isArray(cachedData.response)) {
          console.log('Сохраняем данные лиг из общего кэша в специальный кэш');
          configService.saveLeagues(cachedData.response);
        }
        
        return {
          status: 'success',
          data: cachedData,
          fromCache: true
        };
      }
      
      console.log('❌ Данные лиг не найдены ни в специальном, ни в общем кэше. Запрашиваем API...');
      
      // Делаем запрос к API и результат будет автоматически сохранен в оба кэша
      return this.get('/leagues', {});
    } catch (error) {
      console.error('❌ Ошибка в getLeagues:', error);
      // В случае ошибки всё равно пытаемся получить данные через API
      return this.get('/leagues', {});
    }
  }

  async getTeams(leagueId, season) {
    console.log(`Вызов метода getTeams для лиги ${leagueId}${season ? `, сезон ${season}` : ''}`);
    
    if (!leagueId) {
      console.error('❌ Ошибка: не указан ID лиги для получения команд');
      return {
        status: 'error',
        message: 'No league ID provided',
        fromCache: false
      };
    }
    
    try {
      // Подготавливаем параметры запроса
      const params = { league: leagueId };
      if (season) {
        params.season = season;
      }
      
      // Проверяем специальный кэш для команд сначала
      const cachedTeams = configService.getTeams(leagueId);
      if (cachedTeams && cachedTeams.response && Array.isArray(cachedTeams.response) && cachedTeams.response.length > 0) {
        console.log(`✅ Используем специальный кэш для команд лиги ${leagueId}, найдено команд:`, cachedTeams.response.length);
        return {
          status: 'success',
          data: cachedTeams,
          fromCache: true
        };
      }
      
      console.log(`❌ Данные в специальном кэше команд для лиги ${leagueId} не найдены или пусты`);
      
      // Проверяем общий кэш API
      const cachedData = configService.getCachedData('/teams', params);
      if (cachedData) {
        console.log(`✅ Используем данные команд лиги ${leagueId}${season ? `, сезон ${season}` : ''} из общего кэша API`);
        
        // Сохраняем в специальный кэш, если есть response и это массив
        if (cachedData.response && Array.isArray(cachedData.response)) {
          console.log(`Сохраняем данные команд лиги ${leagueId} из общего кэша в специальный кэш`);
          configService.saveTeams(leagueId, cachedData);
        }
        
        return {
          status: 'success',
          data: cachedData,
          fromCache: true
        };
      }
      
      console.log(`❌ Данные команд лиги ${leagueId}${season ? `, сезон ${season}` : ''} не найдены ни в специальном, ни в общем кэше. Запрашиваем API...`);
      
      // Делаем запрос к API и результат будет автоматически сохранен в оба кэша
      return this.get('/teams', params);
    } catch (error) {
      console.error(`❌ Ошибка в getTeams для лиги ${leagueId}${season ? `, сезон ${season}` : ''}:`, error);
      // В случае ошибки всё равно пытаемся получить данные через API
      const params = { league: leagueId };
      if (season) {
        params.season = season;
      }
      return this.get('/teams', params);
    }
  }

  async getFixtures(leagueId, season) {
    console.log(`Вызов метода getFixtures для лиги ${leagueId}, сезон ${season}`);
    
    if (!leagueId || !season) {
      console.error('❌ Ошибка: не указан ID лиги или сезон для получения матчей');
      return {
        status: 'error',
        message: 'No league ID or season provided',
        fromCache: false
      };
    }
    
    try {
      // Проверяем специальный кэш для матчей сначала
      const params = { league: leagueId, season };
      const cachedFixtures = configService.getFixtures(params);
      if (cachedFixtures && cachedFixtures.response && Array.isArray(cachedFixtures.response) && cachedFixtures.response.length > 0) {
        console.log(`✅ Используем специальный кэш для матчей лиги ${leagueId}, сезон ${season}, найдено матчей:`, cachedFixtures.response.length);
        return {
          status: 'success',
          data: cachedFixtures,
          fromCache: true
        };
      }
      
      console.log(`❌ Данные в специальном кэше матчей для лиги ${leagueId}, сезон ${season} не найдены или пусты`);
      
      // Проверяем общий кэш API
      const cachedData = configService.getCachedData('/fixtures', params);
      if (cachedData) {
        console.log(`✅ Используем данные матчей лиги ${leagueId}, сезон ${season} из общего кэша API`);
        
        // Сохраняем в специальный кэш, если есть response и это массив
        if (cachedData.response && Array.isArray(cachedData.response)) {
          console.log(`Сохраняем данные матчей лиги ${leagueId}, сезон ${season} из общего кэша в специальный кэш`);
          configService.saveFixtures(params, cachedData);
        }
        
        return {
          status: 'success',
          data: cachedData,
          fromCache: true
        };
      }
      
      console.log(`❌ Данные матчей лиги ${leagueId}, сезон ${season} не найдены ни в специальном, ни в общем кэше. Запрашиваем API...`);
      
      // Делаем запрос к API и результат будет автоматически сохранен в оба кэша
      return this.get('/fixtures', params);
    } catch (error) {
      console.error(`❌ Ошибка в getFixtures для лиги ${leagueId}, сезон ${season}:`, error);
      // В случае ошибки всё равно пытаемся получить данные через API
      return this.get('/fixtures', { league: leagueId, season });
    }
  }

  async getStanding(leagueID, season) {
    try {
      
      const params = { league: leagueID, season : season};
      const response = await this.get('/standings', params);
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'standings',
        params: { leagueID, season},
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in standings:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }

  async getPredictor(fixtureID) {
    try {
      
      const params = { fixture: fixtureID};
      const response = await this.get('/predictions', params);
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'predictions',
        params: { fixtureID},
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in standings:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }
  
  async getStandings(leagueId, season) {
    return this.get('/standings', { league: leagueId, season });
  }
  
  async getStatistics(leagueId, season, teamId) {
    return this.get('/teams/statistics', { league: leagueId, season, team: teamId });
  }

  async fetchFixtures(leagueId, fromDate, toDate, season = 2024) {
    try {
  
      const params = { league: leagueId, season: season, from: fromDate.toISOString().split('T')[0], to: toDate.toISOString().split('T')[0] };
      const response = await this.get('/fixtures', params);
      
      if (response.status === 'error') {
        console.error('Error fetching fixtures by date range:', response.message);
        return response;
      }
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'fixtures',
        params: {leagueId, season, fromDate: fromDate.toISOString().split('T')[0], toDate: toDate.toISOString().split('T')[0] },
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in FixturesManager:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures'
      };
    }
  }

  async getStatisticsH2H(fixtureID) {
    try {
      
      const params = { fixture: fixtureID};
      const response = await this.get('/fixtures/statistics', params);
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'fixtures/statistics',
        params: { fixtureID},
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in odds:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }

  async getHeadToHeadMatches(h2hParam, fromDate, toDate) {
    try {
      
      const params = { h2h: h2hParam, from: fromDate.toISOString().split('T')[0], to: toDate.toISOString().split('T')[0]};
      const response = await this.get('/fixtures/headtohead', params);
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'fixtures/headtohead',
        params: { h2h: h2hParam, from: fromDate, to: toDate},
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in h2h matches:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }

  async getOddsByLeague(leagueId, season = 2024, fixtureID) {
    try {
      
      const params = { league: leagueId, season: season, fixture: fixtureID};
      const response = await this.get('/odds', params);
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'odds',
        params: {leagueId, season, fixtureID},
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in odds:', error);
      return {
        status: 'error',
        message: error.message,
        data: null
      };
    }
  }

  clearCache() {
    console.log('Clearing cache...');
    localStorage.clear();
    console.log('Cache cleared.');
  }
}

export default ApiService; 